import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { ArrowLeft, Plus, X, MapPin, Repeat, Calendar, Map } from 'lucide-react';
import AddressAutocomplete from '../components/AddressAutocomplete';
import ContactAutocomplete from '../components/ContactAutocomplete';
import RouteVisualizationMap from '../components/RouteVisualizationMap';

const RideEntry = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useContext(AuthContext);
  const isEdit = !!id;
  const isAdmin = user?.role === 'admin';
  
  // Check for pre-filled contact data from navigation state
  const prefillContact = location.state?.prefillContact;

  const [formData, setFormData] = useState({
    date: new Date().toISOString().slice(0, 16),
    client_name: prefillContact?.client_name || '',
    client_phone: prefillContact?.client_phone || '',
    client_email: prefillContact?.client_email || '',
    pickup_location: '',
    dropoff_location: '',
    stops: [], // Intermediate stops
    miles_driven: '',
    time_hours: '',
    earnings: '',
    tips: '',
    notes: '',
    assigned_to: '',
    available_for_claim: false,
    // Recurring ride fields
    is_recurring: false,
    recurring_pattern: '',
    recurring_days: [],
    recurring_end_date: ''
  });
  const [loading, setLoading] = useState(false);
  const [fetchingRide, setFetchingRide] = useState(isEdit);
  const [drivers, setDrivers] = useState([]);
  const [priceEstimate, setPriceEstimate] = useState(null);
  const [showRouteMap, setShowRouteMap] = useState(false);

  // Check if we have enough location data to show route map
  const canShowRouteMap = formData.pickup_location && formData.dropoff_location;

  useEffect(() => {
    if (isEdit) {
      fetchRide();
    }
    // Both admin and drivers can see other drivers for assignment
    fetchDrivers();
  }, [id]);

  const fetchDrivers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setDrivers(response.data);
    } catch (error) {
      console.error('Failed to load drivers');
    }
  };

  const fetchRide = async () => {
    try {
      const response = await axios.get(`${API}/rides/${id}`);
      const ride = response.data;
      setFormData({
        date: new Date(ride.date).toISOString().slice(0, 16),
        client_name: ride.client_name,
        client_phone: ride.client_phone || '',
        client_email: ride.client_email || '',
        pickup_location: ride.pickup_location,
        dropoff_location: ride.dropoff_location,
        stops: ride.stops || [], // Ensure stops is always an array
        miles_driven: ride.miles_driven?.toString() || '',
        time_hours: ride.time_hours?.toString() || '',
        earnings: ride.earnings?.toString() || '',
        tips: ride.tips?.toString() || '0',
        notes: ride.notes || '',
        assigned_to: ride.assigned_to || '',
        available_for_claim: ride.available_for_claim || false,
        is_recurring: ride.is_recurring || false,
        recurring_pattern: ride.recurring_pattern || '',
        recurring_days: ride.recurring_days || [],
        recurring_end_date: ride.recurring_end_date || ''
      });
    } catch (error) {
      toast.error('Failed to load ride details');
      navigate('/rides');
    } finally {
      setFetchingRide(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const newData = { ...prev, [name]: value };
      
      // Auto-calculate distance and time when both addresses are filled
      if ((name === 'pickup_location' || name === 'dropoff_location') && 
          newData.pickup_location && newData.dropoff_location) {
        calculateDistanceAndTime(
          name === 'pickup_location' ? value : newData.pickup_location,
          name === 'dropoff_location' ? value : newData.dropoff_location
        );
      }
      
      return newData;
    });
  };

  // Handle contact selection from autocomplete
  const handleSelectContact = (contact) => {
    setFormData(prev => ({
      ...prev,
      client_name: contact.name || '',
      client_email: contact.email || '',
      client_phone: contact.phone || ''
    }));
    toast.success(`Contact "${contact.name}" selected - details auto-filled!`, { duration: 2000 });
  };

  // Handle adding/removing intermediate stops
  const addStop = () => {
    setFormData(prev => {
      const newStops = [...prev.stops, { address: '', notes: '' }];
      // Recalculate price if we have distance data
      if (prev.miles_driven && prev.time_hours) {
        setTimeout(() => calculatePrice(
          parseFloat(prev.miles_driven), 
          parseFloat(prev.time_hours),
          newStops.filter(s => s && (s.address || (typeof s === 'string' && s.trim()))).length
        ), 100);
      }
      return { ...prev, stops: newStops };
    });
  };

  const removeStop = (index) => {
    setFormData(prev => {
      const newStops = prev.stops.filter((_, i) => i !== index);
      // Recalculate price if we have distance data
      if (prev.miles_driven && prev.time_hours) {
        setTimeout(() => calculatePrice(
          parseFloat(prev.miles_driven), 
          parseFloat(prev.time_hours),
          newStops.filter(s => s && (s.address || (typeof s === 'string' && s.trim()))).length
        ), 100);
      }
      return { ...prev, stops: newStops };
    });
  };

  const updateStop = (index, field, value) => {
    setFormData(prev => {
      const newStops = prev.stops.map((stop, i) => 
        i === index ? { ...stop, [field]: value } : stop
      );
      
      // If updating an address and we have distance data, recalculate price
      if (field === 'address' && prev.miles_driven && prev.time_hours) {
        const validStopsCount = newStops.filter(s => s && (s.address || (typeof s === 'string' && s.trim()))).length;
        setTimeout(() => calculatePrice(
          parseFloat(prev.miles_driven), 
          parseFloat(prev.time_hours),
          validStopsCount
        ), 100);
      }
      
      return { ...prev, stops: newStops };
    });
  };

  // Handle recurring day selection
  const toggleRecurringDay = (day) => {
    setFormData(prev => ({
      ...prev,
      recurring_days: prev.recurring_days.includes(day)
        ? prev.recurring_days.filter(d => d !== day)
        : [...prev.recurring_days, day].sort()
    }));
  };

  const dayLabels = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  const calculateDistanceAndTime = async (pickup, dropoff) => {
    if (!pickup || !dropoff || !window.google) return;

    try {
      const service = new window.google.maps.DistanceMatrixService();
      
      service.getDistanceMatrix(
        {
          origins: [pickup],
          destinations: [dropoff],
          travelMode: 'DRIVING',
          unitSystem: window.google.maps.UnitSystem.IMPERIAL
        },
        (response, status) => {
          if (status === 'OK' && response.rows[0].elements[0].status === 'OK') {
            const element = response.rows[0].elements[0];
            
            // Extract distance in miles
            const distanceText = element.distance.text; // e.g., "12.5 mi"
            const miles = parseFloat(distanceText.replace(' mi', ''));
            
            // Extract duration in hours
            const durationInSeconds = element.duration.value;
            const hours = (durationInSeconds / 3600).toFixed(2); // Convert to hours
            
            // Update form data
            setFormData(prev => ({
              ...prev,
              miles_driven: miles.toString(),
              time_hours: hours.toString()
            }));
            
            // Calculate price automatically
            calculatePrice(miles, parseFloat(hours));
            
            toast.success(`Distance: ${miles} miles, Time: ${hours} hours`, {
              duration: 3000
            });
          } else {
            console.error('Distance calculation failed:', status);
          }
        }
      );
    } catch (error) {
      console.error('Error calculating distance:', error);
    }
  };

  const calculatePrice = async (miles, hours, numStops = null) => {
    try {
      // Use provided numStops or get from current form data
      const stopsCount = numStops !== null ? numStops : (formData.stops || []).filter(s => s && (s.address || (typeof s === 'string' && s.trim()))).length;
      
      const response = await axios.post(`${API}/calculate-price`, { 
        distance_miles: miles, 
        time_hours: hours,
        num_stops: stopsCount
      });
      setPriceEstimate(response.data);
      
      // Auto-fill the earnings field with calculated price
      setFormData(prev => ({
        ...prev,
        earnings: response.data.final_price.toString()
      }));
      
      let priceMessage = `Estimated Price: $${response.data.final_price.toFixed(2)}`;
      if (stopsCount > 0) {
        priceMessage += ` (includes ${stopsCount} stop${stopsCount > 1 ? 's' : ''}: +$${response.data.stops_fee.toFixed(2)})`;
      }
      
      toast.success(priceMessage, { 
        duration: 4000 
      });
    } catch (error) {
      console.error('Error calculating price:', error);
    }
  };

  const handleSubmit = async (e, addAnother = false) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        date: new Date(formData.date).toISOString(),
        client_name: formData.client_name,
        client_phone: formData.client_phone || "",
        client_email: formData.client_email || "",
        pickup_location: formData.pickup_location,
        dropoff_location: formData.dropoff_location,
        miles_driven: parseFloat(formData.miles_driven) || 0,
        time_hours: parseFloat(formData.time_hours) || 0,
        earnings: parseFloat(formData.earnings) || 0,
        tips: parseFloat(formData.tips) || 0,
        notes: formData.notes || "",
        assigned_to: formData.assigned_to || null,
        available_for_claim: formData.available_for_claim || false,
        stops: (formData.stops || [])
          .filter(s => s && (s.address || (typeof s === 'string' && s.trim())))
          .map(s => typeof s === 'string' ? { address: s, notes: '' } : s),
      };

      // Only add recurring fields if recurring is enabled and properly configured
      if (formData.is_recurring && formData.recurring_pattern && formData.recurring_end_date) {
        payload.is_recurring = true;
        payload.recurring_pattern = formData.recurring_pattern;
        payload.recurring_days = formData.recurring_days || [];
        payload.recurring_end_date = new Date(formData.recurring_end_date + 'T23:59:59').toISOString();
      }

      if (isEdit) {
        await axios.put(`${API}/rides/${id}`, payload);
        toast.success('Ride updated successfully!');
        navigate('/rides');
      } else {
        const response = await axios.post(`${API}/rides`, payload);
        
        // Check if it was a recurring ride creation (has count field)
        if (response.data && response.data.count && response.data.count > 1) {
          toast.success(`Created ${response.data.count} recurring rides!`, { duration: 4000 });
        } else {
          toast.success('Ride added successfully!');
        }
        
        if (addAnother) {
          setFormData({
            date: new Date().toISOString().slice(0, 16),
            client_name: '',
            client_phone: '',
            client_email: '',
            pickup_location: '',
            dropoff_location: '',
            stops: [],
            miles_driven: '',
            time_hours: '',
            earnings: '',
            tips: '',
            notes: '',
            assigned_to: '',
            available_for_claim: false,
            is_recurring: false,
            recurring_pattern: '',
            recurring_days: [],
            recurring_end_date: ''
          });
        } else {
          navigate('/rides');
        }
      }
    } catch (error) {
      console.error('Ride save error:', error);
      const errorMessage = error.response?.data?.detail;
      if (typeof errorMessage === 'string') {
        toast.error(errorMessage);
      } else if (Array.isArray(errorMessage)) {
        toast.error(errorMessage[0]?.msg || 'Validation error');
      } else {
        toast.error('Failed to save ride');
      }
    } finally {
      setLoading(false);
    }
  };

  if (fetchingRide) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-gray-600">Loading ride...</div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl" data-testid="ride-entry-page">
      <Button
        variant="ghost"
        onClick={() => navigate('/rides')}
        className="mb-4"
        data-testid="back-button"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Rides
      </Button>

      <Card>
        <CardHeader>
          <CardTitle className="text-2xl">{isEdit ? 'Edit Ride' : 'Add New Ride'}</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={(e) => handleSubmit(e, false)} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="date">Date & Time *</Label>
                <Input
                  id="date"
                  name="date"
                  type="datetime-local"
                  value={formData.date}
                  onChange={handleChange}
                  required
                  className="form-input"
                  data-testid="ride-date-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="client_name">Client Name *</Label>
                <ContactAutocomplete
                  value={formData.client_name}
                  onChange={handleChange}
                  onSelectContact={handleSelectContact}
                  placeholder="Start typing client name..."
                  required={true}
                  dataTestId="ride-client-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="client_phone">Client Phone</Label>
                <Input
                  id="client_phone"
                  name="client_phone"
                  type="tel"
                  placeholder="(555) 123-4567"
                  value={formData.client_phone}
                  onChange={handleChange}
                  className="form-input"
                  data-testid="ride-phone-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="client_email">Client Email</Label>
                <Input
                  id="client_email"
                  name="client_email"
                  type="email"
                  placeholder="client@email.com"
                  value={formData.client_email}
                  onChange={handleChange}
                  className="form-input"
                  data-testid="ride-email-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="pickup_location">Pickup Location *</Label>
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-green-600 flex-shrink-0" />
                  <div className="flex-1">
                    <AddressAutocomplete
                      id="pickup_location"
                      name="pickup_location"
                      placeholder="Start typing address..."
                      value={formData.pickup_location}
                      onChange={handleChange}
                      required={true}
                      dataTestId="ride-pickup-input"
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="dropoff_location">Dropoff Location *</Label>
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-red-600 flex-shrink-0" />
                  <div className="flex-1">
                    <AddressAutocomplete
                      id="dropoff_location"
                      name="dropoff_location"
                      placeholder="Start typing address..."
                      value={formData.dropoff_location}
                      onChange={handleChange}
                      required={true}
                      dataTestId="ride-dropoff-input"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Intermediate Stops */}
            {formData.stops && formData.stops.length > 0 && (
              <div className="space-y-3 pl-6 border-l-2 border-blue-200">
                <Label className="text-sm font-medium text-blue-700">Intermediate Stops</Label>
                {formData.stops.map((stop, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-blue-500 flex-shrink-0" />
                    <Badge variant="outline" className="w-6 h-6 flex items-center justify-center text-xs">
                      {index + 1}
                    </Badge>
                    <div className="flex-1">
                      <AddressAutocomplete
                        id={`stop_${index}`}
                        name={`stop_${index}`}
                        placeholder={`Stop ${index + 1} address...`}
                        value={stop.address}
                        onChange={(e) => updateStop(index, 'address', e.target.value)}
                        dataTestId={`ride-stop-${index}-input`}
                      />
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeStop(index)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}

            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addStop}
              className="flex items-center gap-2"
              data-testid="add-stop-btn"
            >
              <Plus className="w-4 h-4" />
              Add Stop
            </Button>

            {/* Route Map Toggle and Display */}
            {canShowRouteMap && (
              <div className="space-y-3">
                <Button
                  type="button"
                  variant={showRouteMap ? "default" : "outline"}
                  size="sm"
                  onClick={() => setShowRouteMap(!showRouteMap)}
                  className="flex items-center gap-2"
                  data-testid="show-route-map-btn"
                >
                  <Map className="w-4 h-4" />
                  {showRouteMap ? 'Hide Route Map' : 'Show Route Map'}
                </Button>

                {showRouteMap && (
                  <RouteVisualizationMap
                    pickup={formData.pickup_location}
                    dropoff={formData.dropoff_location}
                    stops={formData.stops.filter(s => s.address)}
                    height="350px"
                  />
                )}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="miles_driven">Miles Driven *</Label>
                <Input
                  id="miles_driven"
                  name="miles_driven"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="15.5"
                  value={formData.miles_driven}
                  onChange={handleChange}
                  required
                  className="form-input"
                  data-testid="ride-miles-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="time_hours">Time (hours) *</Label>
                <Input
                  id="time_hours"
                  name="time_hours"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="1.5"
                  value={formData.time_hours}
                  onChange={handleChange}
                  required
                  className="form-input"
                  data-testid="ride-time-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="earnings">
                  Earnings ($) *
                  {priceEstimate && (
                    <span className="text-xs text-gray-500 font-normal ml-2">
                      (Auto-calculated: ${priceEstimate.final_price.toFixed(2)} - You can change this)
                    </span>
                  )}
                </Label>
                <Input
                  id="earnings"
                  name="earnings"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="50.00"
                  value={formData.earnings}
                  onChange={handleChange}
                  required
                  className="form-input"
                  data-testid="ride-earnings-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="tips">Tips ($) *</Label>
                <Input
                  id="tips"
                  name="tips"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="10.00"
                  value={formData.tips}
                  onChange={handleChange}
                  required
                  className="form-input"
                  data-testid="ride-tips-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                name="notes"
                placeholder="Additional notes about this ride..."
                value={formData.notes}
                onChange={handleChange}
                rows={3}
                className="form-input"
                data-testid="ride-notes-input"
              />
            </div>

            {/* Recurring Ride Options */}
            {!isEdit && (
              <div className="border rounded-lg p-4 bg-blue-50/50">
                <div className="flex items-center gap-3 mb-4">
                  <Repeat className="w-5 h-5 text-blue-600" />
                  <div className="flex-1">
                    <Label className="text-base font-medium">Make this a recurring ride</Label>
                    <p className="text-sm text-gray-500">Schedule this ride to repeat automatically</p>
                  </div>
                  <Switch
                    checked={formData.is_recurring}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_recurring: checked }))}
                    data-testid="recurring-toggle"
                  />
                </div>

                {formData.is_recurring && (
                  <div className="space-y-4 pt-4 border-t">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Repeat Pattern</Label>
                        <Select
                          value={formData.recurring_pattern}
                          onValueChange={(value) => setFormData(prev => ({ ...prev, recurring_pattern: value }))}
                        >
                          <SelectTrigger data-testid="recurring-pattern">
                            <SelectValue placeholder="Select frequency" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="daily">Daily</SelectItem>
                            <SelectItem value="weekly">Weekly</SelectItem>
                            <SelectItem value="biweekly">Every 2 Weeks</SelectItem>
                            <SelectItem value="monthly">Monthly</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label>End Date</Label>
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-gray-500" />
                          <Input
                            type="date"
                            value={formData.recurring_end_date}
                            onChange={(e) => setFormData(prev => ({ ...prev, recurring_end_date: e.target.value }))}
                            min={new Date().toISOString().split('T')[0]}
                            className="flex-1"
                            data-testid="recurring-end-date"
                          />
                        </div>
                      </div>
                    </div>

                    {formData.recurring_pattern === 'weekly' && (
                      <div className="space-y-2">
                        <Label>Repeat on these days</Label>
                        <div className="flex flex-wrap gap-2">
                          {dayLabels.map((day, index) => (
                            <button
                              key={day}
                              type="button"
                              onClick={() => toggleRecurringDay(index)}
                              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                                formData.recurring_days.includes(index)
                                  ? 'bg-blue-600 text-white'
                                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                              }`}
                              data-testid={`recurring-day-${day}`}
                            >
                              {day}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {formData.recurring_pattern && formData.recurring_end_date && (
                      <div className="text-sm text-blue-700 bg-blue-100 rounded-lg p-3">
                        <strong>Preview:</strong> This ride will be created{' '}
                        {formData.recurring_pattern === 'daily' && 'every day'}
                        {formData.recurring_pattern === 'weekly' && (
                          formData.recurring_days.length > 0
                            ? `every ${formData.recurring_days.map(d => dayLabels[d]).join(', ')}`
                            : 'every week'
                        )}
                        {formData.recurring_pattern === 'biweekly' && 'every 2 weeks'}
                        {formData.recurring_pattern === 'monthly' && 'every month'}
                        {' '}until {new Date(formData.recurring_end_date).toLocaleDateString()}.
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Assignment Options - Available for all users */}
            <div className="border-t pt-6 space-y-4">
              <h3 className="font-semibold text-lg">
                {isAdmin ? 'Assignment Options (Admin)' : 'Ride Assignment'}
              </h3>
              
              {!isAdmin && (
                <p className="text-sm text-gray-600">
                  Assign this ride to yourself to record it as completed and keep 100% of the earnings.
                </p>
              )}
              
              <div className="space-y-2">
                <Label htmlFor="assigned_to">Assign to Driver</Label>
                <Select
                  value={formData.assigned_to || "none"}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, assigned_to: value === "none" ? "" : value }))}
                >
                  <SelectTrigger data-testid="assign-driver-select">
                    <SelectValue placeholder="Select driver (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No driver assigned</SelectItem>
                    <SelectItem value={user?.id}>Myself ({user?.name}) - Keep 100%</SelectItem>
                    {drivers.filter(d => d.id !== user?.id).map((driver) => (
                      <SelectItem key={driver.id} value={driver.id}>
                        {driver.name} ({driver.email})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {formData.assigned_to && formData.assigned_to === user?.id && (
                  <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-sm text-green-700 font-medium">
                      ✓ You keep 100% - ${formData.earnings ? parseFloat(formData.earnings).toFixed(2) : '0.00'}
                    </p>
                    <p className="text-xs text-green-600 mt-1">
                      This ride will be marked as completed and added to your earnings.
                    </p>
                  </div>
                )}
                {formData.assigned_to && formData.assigned_to !== user?.id && !isAdmin && (
                  <p className="text-xs text-blue-600">
                    ℹ You'll earn 20% commission, assigned driver gets 80%
                  </p>
                )}
              </div>

              {/* Map Link */}
              {formData.pickup_location && formData.dropoff_location && (
                <div className="pt-2">
                  <a
                    href={`https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(formData.pickup_location)}&destination=${encodeURIComponent(formData.dropoff_location)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline text-sm flex items-center gap-1"
                  >
                    📍 View Route on Google Maps
                  </a>
                </div>
              )}

              <div className="flex items-center space-x-2">
                <Switch
                  id="available_for_claim"
                  checked={formData.available_for_claim}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, available_for_claim: checked }))}
                  data-testid="available-for-claim-switch"
                />
                <Label htmlFor="available_for_claim" className="cursor-pointer">
                  Make available for drivers to claim
                </Label>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                type="submit"
                className="btn-primary flex-1"
                disabled={loading}
                data-testid="save-ride-button"
              >
                {loading ? 'Saving...' : (isEdit ? 'Update Ride' : 'Save Ride')}
              </Button>
              {!isEdit && (
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1"
                  onClick={(e) => handleSubmit(e, true)}
                  disabled={loading}
                  data-testid="save-add-another-button"
                >
                  Save & Add Another
                </Button>
              )}
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default RideEntry;
